import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, take} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {ProjectImage, ProjectImageResponse} from "../../models/project/project-image";
import {IAttachment, IPageableAttachments} from "../../models/project/project-data";
import {ToastService} from "../toast/toast.service";
import {TranslateService} from "@ngx-translate/core";

/**
 * Service for project files, either images or attachments
 *
 * <p>The main focus of this service is to provide methods for uploading, retrieving, and deleting project images. But
 * it can also upload project attachment files.
 */
@Injectable({providedIn: 'root'})
export class ProjectFileService {
    private readonly httpClient = inject(HttpClient);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);

    /**
     * Gets the list of project images.
     *
     * @param projectId The project the images belong to.
     * @returns The list of project images.
     */
    public listImages(projectId: number): Observable<ProjectImageResponse> {
        return this.httpClient.get<ProjectImageResponse>(this.baseImageUrl(projectId)).pipe(
            take(1),
            catchError((error) => this.handleError(error, 'ERRORS.project_images_not_retrieved'))
        );
    }

    /**
     * Gets a single project image.
     *
     * @param id The id of the images.
     * @param projectId The project the images belong to.
     * @returns The project image.
     */
    public getImage(id: number, projectId: number, isPrimary: boolean): Observable<ProjectImage> {
        return this.httpClient.get<ProjectImage>(this.imageUrl(id, projectId, isPrimary)).pipe(
            take(1),
            catchError((error) => this.handleError(error, 'ERRORS.project_image_not_retrieved'))
        );
    }

    /**
     * Deletes a single project image.
     *
     * @param id The id of the images.
     * @param projectId The project the images belong to.
     * @returns The delete result.
     */
    public deleteImage(id: number, projectId: number): Observable<ProjectImage> {
        return this.httpClient.delete<ProjectImage>(this.imageUrl(id, projectId)).pipe(
            catchError((error) => this.handleError(error, 'ERRORS.project_image_not_retrieved'))
        );
    }

    /**
     * Uploads a project image.
     *
     * @param projectId The id of the project.
     * @param file The file to upload.
     * @param isPrimary Whether the upload should replace the project's primary (pinned) image instead of adding a gallery image.
     * @returns The uploaded project image.
     */
    public uploadImage(projectId: number, file: FormData, isPrimary: boolean): Observable<ProjectImage> {
        return this.httpClient.post<ProjectImage>(this.baseImageUrl(projectId, isPrimary), file).pipe(
            take(1),
            catchError((error) =>  {
                if (error.status === 413) {
                    return this.handleError(error, 'ERRORS.project_image_max_upload_failed');
                }
                return this.handleError(error, 'ERRORS.project_image_upload_failed');
            })
        );
    }

    /**
     * Uploads a project document.
     *
     * @param projectId The id of the project.
     * @param file The file to upload.
     * @returns The uploaded project attachment.
     */
    public uploadDocument(projectId: number, file: FormData): Observable<IAttachment> {
        return this.httpClient.post<IAttachment>(this.baseDocumentUrl(projectId), file).pipe(
            take(1),
            catchError((error) => {
                if (error.status === 413) {
                    return this.handleError(error, 'ERRORS.project_document_max_upload_failed');
                }
                return this.handleError(error, 'ERRORS.project_document_upload_failed');
            })
        );
    }

    /**
     * Gets a page of project documents.
     *
     * @param projectId The id of the project.
     * @param page The page number to retrieve.
     * @param size The result set size.
     * @param sortBy The sort field (originalFileName, uniqueId or createdAt).
     * @param ascending The sort direction.
     * @returns The requested page of project attachments.
     */
    public listDocuments(
        projectId: number,
        page = 0,
        size = 25,
        sortBy: 'originalFileName' | 'uniqueId' | 'createdAt' = 'originalFileName',
        ascending = true
    ): Observable<IPageableAttachments> {
        const params = {page: `${page}`, size: `${size}`, sortBy, ascending: `${ascending}`};
        return this.httpClient.get<IPageableAttachments>(this.baseDocumentUrl(projectId), {params}).pipe(
            take(1),
            catchError((error) => this.handleError(error, 'ERRORS.project_documents_not_retrieved'))
        );
    }

    /**
     * Deletes a single project attachment.
     *
     * @param uuid The id of the attachment.
     * @param projectId The project the attachment belongs to.
     * @returns The delete result, which is an empty observable.
     */
    public deleteDocument(uuid: string, projectId: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.baseDocumentUrl(projectId)}/${uuid}`).pipe(
            catchError((error) => this.handleError(error, 'ERRORS.project_document_not_deleted'))
        );
    }
    /**
     * Url for a single project image.
     *
     * @param id The id of the images.
     * @param projectId The project the images belong to.
     * @param isPrimary Whether the url should address the project's primary (pinned) image instead of a gallery image.
     * @returns The url for the project image.
     */
    public imageUrl(id: number, projectId: number, isPrimary = false): string {
        const url = `${environment.apiUrl}/projects/${projectId}/images/${id}`;
        return isPrimary ? `${url}?isPrimary=true` : url;
    }

    /**
     * Stream url for a single project image.
     *
     * @param id The id of the images.
     * @param projectId The project the images belong to.
     * @param isPrimary Whether the url should address the project's primary (pinned) image instead of a gallery image.
     * @returns The stream url for the project image.
     */
    public imageStreamUrl(id: number, projectId: number, isPrimary = false): string {
        const url = `${this.imageUrl(id, projectId)}/stream`;
        return isPrimary ? `${url}?isPrimary=true` : url;
    }

    private baseImageUrl(projectId: number, isPrimary = false): string {
        const url = `${environment.apiUrl}/projects/${projectId}/images`;
        return isPrimary ? `${url}?isPrimary=true` : url;
    }

    private baseDocumentUrl(projectId: number): string {
        return `${environment.apiUrl}/projects/${projectId}/attachments`;
    }

    /**
     * Handles an http error and returns an observable that throws the error.
     *
     * <p>As a side effect, an error toast is shown to the user.
     *
     * @param error Http error response.
     * @param translationKey Translation key for the error message.
     * @returns An observable that throws the error.
     */
    private handleError(error: HttpErrorResponse, translationKey: string): Observable<never> {
        console.error(error);
        this.toastService.openError(error.status ?? 0, this.translateService.instant(translationKey));
        return throwError(() => error);
    }
}
